import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClientesBusqueda } from '@/hooks/useOrdenesServicio'
import type { ClienteOpcion } from '@/types'

interface Props {
  dark: boolean
  value?: string          // cuenta seleccionada (para mostrarla)
  onSelect: (c: ClienteOpcion) => void
}

/** Buscador de clientes con debounce (Dataverse) + dropdown de resultados.
 *  No da de alta clientes: eso lo hace administración en D365. */
export function ClientePicker({ dark, value, onSelect }: Props) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 300)
    return () => clearTimeout(id)
  }, [q])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const { data: clientes = [], isFetching } = useClientesBusqueda(debounced, open)

  const inputCls = `w-full min-h-[38px] px-3 rounded-lg border text-[13px] ${
    dark ? 'bg-white/5 border-white/15 text-slate-100 placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800'
  }`
  const menuCls = `absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border shadow-xl ${
    dark ? 'bg-slate-800 border-white/15' : 'bg-white border-slate-200'
  }`
  const itemCls = `w-full text-left px-3 py-2 cursor-pointer transition-colors ${dark ? 'hover:bg-white/10' : 'hover:bg-slate-50'}`
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-800'

  const pick = (c: ClienteOpcion) => {
    onSelect(c)
    setQ('')
    setDebounced('')
    setOpen(false)
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        placeholder={value ? value : t('ordenesServicio.form.buscarCliente')}
        className={inputCls}
      />
      {open && (
        <div className={menuCls}>
          {isFetching ? (
            <p className={`px-3 py-2 text-[12px] ${mutedTxt}`}>…</p>
          ) : clientes.length === 0 ? (
            <p className={`px-3 py-2 text-[12px] ${mutedTxt}`}>{t('ordenesServicio.form.sinClientes')}</p>
          ) : (
            clientes.map((c, i) => (
              <button key={`${c.cuenta}-${i}`} type="button" className={itemCls} onClick={() => pick(c)}>
                <span className={`block text-[13px] font-semibold truncate ${mainTxt}`}>{c.nombre || c.cuenta}</span>
                <span className="flex items-center gap-1.5">
                  <span className={`text-[12px] tabular-nums ${mutedTxt}`}>{c.cuenta}</span>
                  {/* La misma razón social existe en varias entidades legales con
                      cuentas distintas: sin la empresa son indistinguibles. */}
                  {c.dataareaid && (
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      dark ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {c.dataareaid}
                    </span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
